import { useState, useCallback, useEffect } from 'react';
import { Contact } from '../../../types';
import { createModuleLogger } from '../../../utils/logger';
import { notify } from '../../../utils/toast';

const log = createModuleLogger('[ContactActions]');

interface UseContactActionsReturn {
    // Estados dos modais
    isFormModalOpen: boolean;
    isDeleteModalOpen: boolean;
    contactToEdit: Contact | null;
    contactToDelete: Contact | null;

    // Handlers de modal
    handleOpenCreateModal: () => void;
    handleOpenEditModal: (contact: Contact) => void;
    handleOpenDeleteModal: (contact: Contact) => void;
    handleCloseModals: () => void;

    // Ações
    handleSaveContact: (contactData: Omit<Contact, 'id'>) => Promise<void>;
    handleConfirmDelete: () => Promise<void>;
    handleDuplicateContact: (contact: Contact) => Promise<void>;
}

interface UseContactActionsProps {
    createContact: (data: Omit<Contact, 'id'>) => Promise<any>;
    updateContact: (id: string, data: Partial<Contact>) => Promise<any>;
    deleteContact: (id: string) => Promise<void>;
}

export function useContactActions({
    createContact,
    updateContact,
    deleteContact
}: UseContactActionsProps): UseContactActionsReturn {
    const [isFormModalOpen, setFormModalOpen] = useState(false);
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [contactToEdit, setContactToEdit] = useState<Contact | null>(null);
    const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);

    useEffect(() => {
        sessionStorage.removeItem('contact_form_modal_open');
    }, []);

    const handleOpenCreateModal = useCallback(() => {
        setContactToEdit(null);
        setFormModalOpen(true);
    }, []);

    const handleOpenEditModal = useCallback((contact: Contact) => {
        setContactToEdit(contact);
        setFormModalOpen(true);
    }, []);

    const handleOpenDeleteModal = useCallback((contact: Contact) => {
        setContactToDelete(contact);
        setDeleteModalOpen(true);
    }, []);

    const handleCloseModals = useCallback(() => {
        setFormModalOpen(false);
        setDeleteModalOpen(false);
        setContactToEdit(null);
        setContactToDelete(null);
        sessionStorage.removeItem('contact_form_data');
    }, []);

    const handleSaveContact = useCallback(async (contactData: Omit<Contact, 'id'>) => {
        try {
            if (contactToEdit) {
                await updateContact(contactToEdit.id, contactData);
                notify.success('Contato atualizado com sucesso!');
            } else {
                await createContact(contactData);
                notify.success('Contato criado com sucesso!');
            }
            handleCloseModals();
        } catch (error) {
            log.error('Failed to save contact:', error);
            notify.error(error instanceof Error ? error.message : 'Erro ao salvar contato.');
            throw error;
        }
    }, [contactToEdit, createContact, updateContact, handleCloseModals]);

    const handleConfirmDelete = useCallback(async () => {
        if (contactToDelete) {
            try {
                await deleteContact(contactToDelete.id);
                notify.success('Contato excluído com sucesso!');
            } catch (error) {
                log.error('Failed to delete contact:', error);
                notify.error('Erro ao excluir contato.');
            }
        }
        handleCloseModals();
    }, [contactToDelete, deleteContact, handleCloseModals]);

    const handleDuplicateContact = useCallback(async (contact: Contact) => {
        try {
            const duplicatedContact: Omit<Contact, 'id'> = {
                name: `${contact.name} (Cópia)`,
                whatsapp: contact.whatsapp,
                phone: contact.phone,
                email: contact.email,
            };
            await createContact(duplicatedContact);
            notify.success('Contato duplicado com sucesso!');
        } catch (error) {
            log.error('Failed to duplicate contact:', error);
            notify.error(error instanceof Error ? error.message : 'Erro ao duplicar contato.');
        }
    }, [createContact]);

    return {
        isFormModalOpen,
        isDeleteModalOpen,
        contactToEdit,
        contactToDelete,
        handleOpenCreateModal,
        handleOpenEditModal,
        handleOpenDeleteModal,
        handleCloseModals,
        handleSaveContact,
        handleConfirmDelete,
        handleDuplicateContact
    };
}
